import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subscribeToPush } from '../lib/pushNotifications';

const isAuthPath = (path) => {
  if (!path) return false;
  if (path === '/login-authentication' || path === '/login' || path.endsWith('/login')) return true;
  if (path.startsWith('/auth/')) return true;
  if (path === '/forgot-password' || path === '/reset-password') return true;
  return false;
};

const PushPrompt = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    // Only show if push is supported but not yet granted, and user hasn't dismissed
    if (!('Notification' in window) || !('PushManager' in window)) return;
    if (Notification.permission === 'granted') return;
    if (Notification.permission === 'denied') return;

    // Check if user dismissed recently (24h cooldown)
    const dismissed = localStorage.getItem('push_prompt_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) return;

    // Show after 2 seconds
    const timer = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (!show || isAuthPath(location?.pathname)) return null;

  const handleEnable = async () => {
    setSubscribing(true);
    const success = await subscribeToPush();
    if (success) {
      setShow(false);
    } else {
      setSubscribing(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('push_prompt_dismissed', String(Date.now()));
    setShow(false);
  };

  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] max-w-md animate-slide-up">
      <div className="rounded-2xl border bg-card border-border p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">🔔</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-foreground" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('push.enableTitle')}
            </p>
            <p className="text-xs mt-1 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('push.enableSubtitle')}
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleEnable}
                disabled={subscribing}
                className="px-4 py-2 rounded-lg text-xs font-bold text-primary-foreground bg-primary hover:bg-primary/90 transition-all disabled:opacity-60"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {subscribing ? t('push.activating') : t('push.enable')}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 rounded-lg text-xs font-bold bg-muted text-muted-foreground hover:bg-muted/80 transition-all"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('push.later')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slide-up { from { opacity: 0; transform: translate(-50%, 20px); } to { opacity: 1; transform: translate(-50%, 0); } }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default PushPrompt;
